/**
 * @description 범용 유틸리티 함수들을 제공합니다.
 */

import mergeWith from 'lodash-es/mergeWith';
import isNil from 'lodash-es/isNil';
import has from 'lodash-es/has';
import { DEFAULT_DATE_FORMAT } from './date';
import transform from 'lodash-es/transform';
import dayjs from 'dayjs';
import isArray from 'lodash-es/isArray';

/**
 * 지정된 시간(밀리초) 동안 실행을 일시 중지합니다.
 *
 * @param ms - 대기 시간 (밀리초)
 * @returns 지정 시간 후 resolve되는 Promise
 *
 * @example
 * ```ts
 * await sleep(1000); // 1초 대기
 * console.log('1초 후 실행');
 * ```
 */
export function sleep(ms: number) {
  return new Promise(resolve => {
    setTimeout(resolve, ms);
  });
}

/**
 * min(포함)과 max(포함) 사이의 무작위 정수를 반환합니다.
 *
 * @param min - 최소값 (포함)
 * @param max - 최대값 (포함)
 * @returns 범위 내 무작위 정수
 *
 * @example
 * ```ts
 * getRandom(1, 10); // 1~10 사이의 정수
 * ```
 */
export function getRandom(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * 열거형의 키 타입 (숫자 제외)
 * @internal
 */
type EnumKeys<Enum> = Exclude<keyof Enum, number>;

/**
 * 열거형을 일반 객체로 변환합니다.
 * 숫자 열거형의 역방향 매핑을 제거합니다.
 *
 * @typeParam Enum - 열거형 타입
 * @param e - 열거형 객체
 * @returns 열거형을 변환한 객체
 *
 * @example
 * ```ts
 * enum Status { Active = 1, Inactive = 2 }
 * enumObject(Status); // { Active: 1, Inactive: 2 }
 * ```
 */
export const enumObject = <Enum extends Record<string, number | string>>(e: Enum) => {
  const result: { [key: string]: string | number } = {};
  // eslint-disable-next-line no-restricted-syntax
  for (const key in e) {
    if (isNaN(parseInt(key, 10))) {
      result[key] = e[key];
    }
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return result as { [K in EnumKeys<Enum>]: Enum[K] };
};

/**
 * 열거형의 키들을 배열로 반환합니다.
 *
 * @typeParam Enum - 열거형 타입
 * @param e - 열거형 객체
 * @returns 열거형 키 배열
 *
 * @example
 * ```ts
 * enum Status { Active = 1, Inactive = 2 }
 * enumKeys(Status); // ['Active', 'Inactive']
 * ```
 */
export const enumKeys = <Enum extends Record<string, number | string>>(e: Enum) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.keys(enumObject(e)) as EnumKeys<Enum>[];
};

/**
 * 열거형의 값들을 배열로 반환합니다 (중복 제거).
 *
 * @typeParam Enum - 열거형 타입
 * @param e - 열거형 객체
 * @returns 열거형 값 배열
 *
 * @example
 * ```ts
 * enum Status { Active = 1, Inactive = 2 }
 * enumValues(Status); // [1, 2]
 * ```
 */
export const enumValues = <Enum extends Record<string, number | string>>(e: Enum) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return [...new Set(Object.values(enumObject(e)))] as Enum[EnumKeys<Enum>][];
};

/**
 * 작업이 완료될 때까지 재시도합니다.
 * 타임아웃 또는 최대 시도 횟수에 도달할 때까지 지정된 간격으로 반복합니다.
 *
 * @typeParam T - 결과 타입
 * @param task - 수행할 비동기 작업. `{ result, isCompleted, error }` 반환
 * @param options - 재시도 옵션
 * @param options.timeoutMs - 최대 대기 시간 (밀리초, 기본값: 10000)
 * @param options.maxTry - 최대 시도 횟수 (0이면 제한 없음)
 * @param options.sleepMs - 재시도 간격 (밀리초, 기본값: 1000)
 * @returns `[결과, { timedOut, tryCnt, elapsed, error }]` 튜플
 *
 * @example
 * ```ts
 * const [result, info] = await doUntil(
 *   async () => {
 *     const data = await fetchData();
 *     return { result: data, isCompleted: data.status === 'ready' };
 *   },
 *   { timeoutMs: 30000, sleepMs: 2000 }
 * );
 *
 * if (info.timedOut) {
 *   console.log('타임아웃 발생');
 * }
 * ```
 */
export async function doUntil<T>(
  task: () =>
    | Promise<{ result?: T; isCompleted: boolean; error?: unknown }>
    | { result?: T; isCompleted: boolean; error?: unknown },
  {
    timeoutMs = 10_000,
    maxTry = 0,
    sleepMs = 1_000,
  }: {
    timeoutMs?: number;
    maxTry?: number;
    sleepMs?: number;
  },
): Promise<[T | undefined, { timedOut: boolean; tryCnt: number; elapsed: number; error?: unknown }]> {
  let result: T | undefined = undefined;
  let tryCnt = 0;
  let timedOut = false;
  let elapsed: number;
  let error: unknown | undefined;

  const dtStart = new Date();
  do {
    ++tryCnt;
    // eslint-disable-next-line no-await-in-loop
    const res = await task();
    if (res.isCompleted) {
      result = res.result;
      break;
    }
    error = res.error;
    elapsed = new Date().getTime() - dtStart.getTime();
    timedOut = !!timeoutMs && timeoutMs <= elapsed;
    if (!!maxTry && maxTry <= tryCnt) {
      timedOut = true;
    }
    // eslint-disable-next-line no-await-in-loop
    await sleep(sleepMs);
  } while (!timedOut);

  elapsed = new Date().getTime() - dtStart.getTime();
  return [result, { timedOut, tryCnt, elapsed, error }];
}

/**
 * 값이 문자열인지 확인하는 타입 가드입니다.
 *
 * @param x - 검사할 값
 * @returns 문자열이면 true
 *
 * @example
 * ```ts
 * if (isString(value)) {
 *   console.log(value.toUpperCase()); // 타입 안전
 * }
 * ```
 */
export function isString(x: unknown): x is string {
  return typeof x === 'string';
}

/**
 * 값이 숫자 형식인지 확인합니다.
 * 숫자로 파싱 가능한 문자열도 true를 반환합니다.
 *
 * @param num - 검사할 값
 * @returns 숫자 형식이면 true
 *
 * @example
 * ```ts
 * isNumeric(123); // true
 * isNumeric('456'); // true
 * isNumeric('abc'); // false
 * isNumeric(null); // false
 * ```
 */
export function isNumeric(num: unknown) {
  return num !== null && num !== '' && !isNaN(Number(num));
}

/**
 * 객체 내의 숫자 형식 문자열을 실제 숫자로 변환합니다.
 * 중첩된 객체도 재귀적으로 처리합니다.
 *
 * @param obj - 변환할 객체
 * @returns 숫자 문자열이 숫자로 변환된 객체
 *
 * @example
 * ```ts
 * stringToNumber({ price: '100', name: 'item' });
 * // { price: 100, name: 'item' }
 * ```
 */
export function stringToNumber(obj: unknown) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return transform(obj as object, function (result: Record<string, unknown>, value: unknown, key: string) {
    result[key] =
      typeof value === 'object'
        ? stringToNumber(value)
        : typeof value === 'string' && isNumeric(value)
          ? Number(value)
          : value;
  });
}

/**
 * 값이 함수인지 확인하는 타입 가드입니다.
 *
 * @param val - 검사할 값
 * @returns 함수이면 true
 */

export function isFunction(val: unknown): val is Function {
  return typeof val === 'function';
}

/**
 * 배열에서 일치하는 값을 찾아 반환하고, 없으면 기본값을 반환합니다.
 *
 * @typeParam T - 값의 타입
 * @param arr - 검색할 배열
 * @param value - 찾을 값
 * @param defaultValue - 기본값
 * @returns 일치하는 값 또는 기본값
 *
 * @example
 * ```ts
 * getMatched(['a', 'b', 'c'], 'b', 'x'); // 'b'
 * getMatched(['a', 'b', 'c'], 'd', 'x'); // 'x'
 * ```
 */
export function getMatched<T>(arr: T[], value: T, defaultValue: T): T {
  return arr.includes(value) ? value : defaultValue;
}

/**
 * 값이 이터러블(반복 가능)인지 확인하는 타입 가드입니다.
 *
 * @typeParam T - 이터러블 요소 타입
 * @param obj - 검사할 값
 * @returns 이터러블이면 true
 */
export function isIterable<T>(obj: unknown): obj is Iterable<T> {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return obj != null && typeof (obj as Record<symbol, unknown>)[Symbol.iterator] === 'function';
}

/**
 * 두 객체를 병합합니다.
 * obj의 속성이 null 또는 undefined이면 src의 값을 사용합니다.
 *
 * @typeParam T - 첫 번째 객체 타입
 * @typeParam U - 두 번째 객체 타입
 * @param obj - 기본 객체 (null/undefined 값은 src로 대체됨)
 * @param src - 소스 객체
 * @returns 병합된 새 객체
 *
 * @example
 * ```ts
 * mergeWithDefault({ a: null, b: 2 }, { a: 1, c: 3 });
 * // { a: 1, b: 2, c: 3 }
 * ```
 */
export function mergeWithDefault<T extends object, U extends object>(obj: T, src: U): T & U {
  return mergeWith({}, obj, src, (objValue, srcValue) => {
    if (objValue === null || objValue === undefined) {
      return srcValue;
    }
    return objValue;
  });
}

/**
 * 두 객체를 병합합니다.
 * src의 값이 undefined여도 obj를 덮어씁니다.
 * (lodash mergeWith는 undefined를 무시하는 문제가 있어 직접 구현)
 *
 * @typeParam T - 첫 번째 객체 타입
 * @typeParam U - 두 번째 객체 타입
 * @param obj - 기본 객체
 * @param src - 소스 객체 (undefined 값도 적용됨)
 * @returns 병합된 새 객체
 *
 * @example
 * ```ts
 * mergeWithIncludingUndefined({ a: 1 }, { a: undefined, b: 2 });
 * // { a: undefined, b: 2 }
 * ```
 */
export function mergeWithIncludingUndefined<T extends object, U extends object>(obj: T, src: U): T & U {
  const result = { ...obj };

  Object.keys(src).forEach(key => {
    if (src.hasOwnProperty(key)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      (result as Record<string, unknown>)[key] = (src as Record<string, unknown>)[key];
    }
  });

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return result as T & U;
}

/**
 * 객체에서 지정된 키들의 속성만 추출합니다.
 * 속성이 없으면 undefined가 포함됩니다.
 * (lodash pick은 undefined를 무시하는 문제가 있어 직접 구현)
 *
 * @typeParam T - 객체 타입
 * @typeParam K - 키 타입
 * @param obj - 대상 객체
 * @param keys - 추출할 키 배열
 * @returns 선택된 속성만 포함된 객체
 *
 * @example
 * ```ts
 * pickWithUndefined({ a: 1, b: 2, c: 3 }, ['a', 'c', 'd']);
 * // { a: 1, c: 3, d: undefined }
 * ```
 */
export function pickWithUndefined<T extends object, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> {
  return keys.reduce(
    (result, key) => {
      (result as Record<K, T[K] | undefined>)[key] = has(obj, key) ? obj[key] : undefined;
      return result;
    },
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion, @typescript-eslint/consistent-type-assertions
    {} as Pick<T, K>,
  );
}

/**
 * 값이 nil(null/undefined) 또는 NaN인지 확인합니다.
 *
 * @param value - 검사할 값
 * @returns nil 또는 NaN이면 true
 */
export function isNilOrNaN(value: unknown) {
  return isNil(value) || isNaN(Number(value));
}

/**
 * 객체의 타임스탬프 값을 날짜 문자열로 변환합니다.
 * 키가 'timestamp'로 끝나거나 2000-01-01 ~ 2100-12-31 범위의 숫자는 날짜로 인식합니다.
 *
 * @param obj - 변환할 객체
 * @returns 타임스탬프가 날짜 문자열로 변환된 객체
 *
 * @example
 * ```ts
 * timestampReplacer({ createdTimestamp: 1609459200000 });
 * // { createdTimestamp: '2021-01-01 00:00:00' }
 * ```
 */
export const timestampReplacer = (obj: unknown) => {
  return transform(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
    obj as object,
    function (result: Record<string, unknown>, value: unknown, key: string) {
      result[key] =
        typeof value === 'object'
          ? timestampReplacer(value)
          : key.toString().endsWith('timestamp') ||
              (isNumeric(value) && Number(value) > 946684800000 && Number(value) < 4133894400000)
            ? // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              dayjs(value as string | number).format(DEFAULT_DATE_FORMAT)
            : value;
    },
    isArray(obj) ? [] : {},
  );
};
